// OT-4982 §14: the Data Store limits shown read-only on Edit Organization. These are the only tenant
// settings the platform reads at ORGANIZATION level (DataStoreLimits.getString checks ORGANIZATION,
// then TENANT, then the datastore-service environment), so they are the only ones an organization
// value can change.
//
// Copied from admin-ui/src/tenant/utils/tenantSettings.ts (the DATA_STORE limit entries and the
// INTEGER branch of toEditValue) — ui and admin-ui are separate apps. Keep the two copies, and the
// defaults in core/datastore-service/src/main/resources/application.yml, in step.
//
// One GET /configs/{key}/ORGANIZATION/{organizationId} per limit returns the effective value: the
// organization's own row, else the gateway's walk-up to the tenant row, else nothing (the gateway's
// environment does not carry datastore.limits.*).

// OT-4982 §15: holders of this privilege may edit the limits. A UI check only — the server does not
// check it (design §10-D5). The privilege is data, created per environment on the Privileges page.
export const EDIT_LIMITS_PRIVILEGE = "ORGANIZATIONS.LIMITS.EDIT";

export interface OrganizationLimit {
  key: string;
  // i18n key of the field label.
  labelKey: string;
  // i18n key of the tooltip.
  descriptionKey: string;
  // The datastore-service default, shown when neither the organization nor the tenant has a row.
  fallbackValue: string;
  // Largest value datastore-service can read for this key: Integer.MAX_VALUE for limits read with
  // DataStoreLimits.getInt, else Number.MAX_SAFE_INTEGER (below Long.MAX_VALUE, and exact in JS). A
  // larger value makes getInt / getLong throw, failing every Data Store request that reads it.
  max: number;
}

const INT_MAX = 2147483647;
const LONG_MAX = Number.MAX_SAFE_INTEGER;

export const ORGANIZATION_LIMITS: OrganizationLimit[] = [
  {
    key: "datastore.limits.max-tables-per-org",
    labelKey: "OrganizationLimits.MaxTablesPerOrg",
    descriptionKey: "OrganizationLimits.MaxTablesPerOrgInfo",
    fallbackValue: "50",
    max: LONG_MAX,
  },
  {
    key: "datastore.limits.max-fields-per-table",
    labelKey: "OrganizationLimits.MaxFieldsPerTable",
    descriptionKey: "OrganizationLimits.MaxFieldsPerTableInfo",
    fallbackValue: "100",
    max: LONG_MAX,
  },
  {
    key: "datastore.limits.max-rows-per-table",
    labelKey: "OrganizationLimits.MaxRowsPerTable",
    descriptionKey: "OrganizationLimits.MaxRowsPerTableInfo",
    fallbackValue: "5000000",
    max: LONG_MAX,
  },
  {
    key: "datastore.limits.max-rows-per-org",
    labelKey: "OrganizationLimits.MaxRowsPerOrg",
    descriptionKey: "OrganizationLimits.MaxRowsPerOrgInfo",
    fallbackValue: "10000000",
    max: LONG_MAX,
  },
  {
    key: "datastore.limits.max-cell-bytes",
    labelKey: "OrganizationLimits.MaxCellBytes",
    descriptionKey: "OrganizationLimits.MaxCellBytesInfo",
    fallbackValue: "4000",
    max: INT_MAX,
  },
  {
    key: "datastore.limits.page-size-limit",
    labelKey: "OrganizationLimits.PageSizeLimit",
    descriptionKey: "OrganizationLimits.PageSizeLimitInfo",
    fallbackValue: "200",
    max: INT_MAX,
  },
  {
    key: "datastore.limits.batch-rows-limit",
    labelKey: "OrganizationLimits.BatchRowsLimit",
    descriptionKey: "OrganizationLimits.BatchRowsLimitInfo",
    fallbackValue: "200",
    max: INT_MAX,
  },
  {
    key: "datastore.limits.max-offset",
    labelKey: "OrganizationLimits.MaxOffset",
    descriptionKey: "OrganizationLimits.MaxOffsetInfo",
    fallbackValue: "100000",
    max: LONG_MAX,
  },
  {
    key: "datastore.limits.import-max-file-bytes",
    labelKey: "OrganizationLimits.ImportMaxFileBytes",
    descriptionKey: "OrganizationLimits.ImportMaxFileBytesInfo",
    fallbackValue: "2147483648",
    max: LONG_MAX,
  },
  {
    key: "datastore.limits.import-chunk-rows",
    labelKey: "OrganizationLimits.ImportChunkRows",
    descriptionKey: "OrganizationLimits.ImportChunkRowsInfo",
    fallbackValue: "10000",
    max: INT_MAX,
  },
  {
    key: "datastore.limits.import-error-report-limit",
    labelKey: "OrganizationLimits.ImportErrorReportLimit",
    descriptionKey: "OrganizationLimits.ImportErrorReportLimitInfo",
    fallbackValue: "30",
    max: INT_MAX,
  },
  {
    key: "datastore.limits.trash-retention-days",
    labelKey: "OrganizationLimits.TrashRetentionDays",
    descriptionKey: "OrganizationLimits.TrashRetentionDaysInfo",
    fallbackValue: "15",
    max: INT_MAX,
  },
];

function hasText(raw: unknown): boolean {
  return raw !== null && raw !== undefined && String(raw).trim() !== "";
}

/**
 * The number to show: value, else defaultValue, else fallbackValue. Accepts only an optional sign and
 * decimal digits, with underscore grouping stripped first — exactly what DataStoreLimits.getLong
 * (Long.parseLong after removing "_") accepts; anything else shows the fallback.
 */
export function toDisplayValue(
  limit: OrganizationLimit,
  config: any | null
): number {
  let raw = limit.fallbackValue;
  if (hasText(config?.value)) {
    raw = String(config.value);
  } else if (hasText(config?.defaultValue)) {
    raw = String(config.defaultValue);
  }
  const text = raw.replace(/_/g, "").trim();
  return /^[+-]?\d+$/.test(text) ? Number(text) : Number(limit.fallbackValue);
}

/**
 * True when an edited limit may be saved: an integer no larger than the limit's max (the input
 * itself enforces min 0).
 */
export function isLimitValueValid(
  limit: OrganizationLimit,
  value: unknown
): boolean {
  return (
    typeof value === "number" && Number.isInteger(value) && value <= limit.max
  );
}

// OT-4982 §16: datastore-service's switch for the tenant ceiling, set per tenant in admin-ui. Read
// at TENANT level only, as the backend reads it.
export const TENANT_CEILING_KEY = "datastore.limits.tenant-ceiling";

/**
 * True when the tenant ceiling is switched on. Mirrors DataStoreLimits.isTenantCeilingEnforced: the
 * row's value, else its defaultValue. No row (or no text) keeps it on — the gateway this page reads
 * through cannot see datastore-service's own default, which is also on; otherwise only "true"
 * (case-insensitive, as Boolean.parseBoolean) keeps it on, and "false" or anything else turns it off.
 */
export function isTenantCeilingEnforced(config: any | null): boolean {
  let raw = "";
  if (hasText(config?.value)) {
    raw = String(config.value);
  } else if (hasText(config?.defaultValue)) {
    raw = String(config.defaultValue);
  } else {
    return true;
  }
  return raw.trim().toLowerCase() === "true";
}

/**
 * True when an organization value stays within the tenant's: an organization may tighten a limit,
 * never loosen it past its tenant (OT-4982 §15).
 */
export function isWithinTenantLimit(
  value: number,
  tenantValue: number
): boolean {
  return value <= tenantValue;
}

/**
 * The PUT /configs payload for an organization override. Spreads the loaded DTO only when it is the
 * organization's own row (so its description, defaultValue and audit fields survive); for an
 * inherited tenant row or a platform value it builds a fresh row, so the tenant row is never copied.
 */
export function buildOrganizationSavePayload(
  limit: OrganizationLimit,
  config: any | null,
  organizationId: string,
  value: number
): any {
  const ownRow = config?.level === "ORGANIZATION" ? config : null;
  return {
    ...(ownRow ?? {}),
    key: limit.key,
    level: "ORGANIZATION",
    ownerId: organizationId,
    type: ownRow?.type ?? "INTEGER",
    value: String(value),
  };
}
